import { useState } from 'react'
import { Copy, MoreVertical, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import { useAuth, useLoadSkillForDuplicate } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { Skill } from '@/types'
import { DeleteSkillDialog } from './DeleteSkillDialog'
import { CreateSkillDialog } from './CreateSkillDialog'

interface SkillActionsMenuProps {
  projectId: string
  skill: Skill
}

export function SkillActionsMenu({ projectId, skill }: SkillActionsMenuProps) {
  const { hasPermission } = useAuth(projectId)
  const [deleting, setDeleting] = useState(false)
  const duplicateSource = useLoadSkillForDuplicate(projectId, skill.id)

  const canDelete = !skill.isReadOnly && hasPermission(Permissions.Project.Skill.Delete, projectId)
  const canDuplicate = skill.isSystem && hasPermission(Permissions.Project.Skill.Create, projectId)

  if (!canDelete && !canDuplicate) {
    return null
  }

  const startDuplicate = () =>
    duplicateSource.mutate(undefined, {
      onError: error => toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to load the skill to duplicate.'),
    })

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label={`Actions for ${skill.displayName}`}
            data-testid={`skill-actions-${skill.name}`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canDuplicate && (
            <DropdownMenuItem
              data-testid={`skill-duplicate-${skill.name}`}
              onSelect={startDuplicate}
              disabled={duplicateSource.isPending}
            >
              <Copy className="h-4 w-4" />
              {duplicateSource.isPending ? 'Loading…' : 'Duplicate'}
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setDeleting(true)}>
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {canDelete && <DeleteSkillDialog projectId={projectId} skill={skill} open={deleting} onOpenChange={setDeleting} />}
      {canDuplicate && duplicateSource.data && (
        <CreateSkillDialog
          projectId={projectId}
          open
          onOpenChange={open => { if (!open) duplicateSource.reset() }}
          title={`Duplicate “${skill.displayName}”`}
          initialValues={{
            displayName: `${skill.displayName} (Copy)`,
            description: skill.description ?? '',
            content: duplicateSource.data.content,
          }}
        />
      )}
    </>
  )
}
